// Las verificaciones de la ficha: la prueba de vida y el comprobante de domicilio de la sesión, la
// identificación del expediente y la autorización de buró del crédito.
import type { LoanCreditResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanCreditResponse';
import type { OnboardingDocument } from '@/api/services/retail-customer/retail-customer.client';
import type {
    CustomerKycSession,
    KycSubCheckOutcome,
    ProofOfAddressResult,
} from '@/api/services/retail-wizard/customerKyc.client';
import type { Gap } from './customer360Gaps';
import type { ReadFailure } from './creditCustomersErrors';

export const KYC_OUTCOME_LABEL: Record<KycSubCheckOutcome, string> = {
    PASSED: 'Aprobada',
    FAILED: 'No aprobó',
    HIT: 'Coincidencia',
    NOT_REPORTED: 'Sin dato',
};

export const KYC_OUTCOME_CHIP_CLASS: Record<KycSubCheckOutcome, string> = {
    PASSED: 'border-success bg-success/10 text-success',
    FAILED: 'border-destructive bg-destructive/10 text-destructive',
    HIT: 'border-warning bg-warning/10 text-warning',
    NOT_REPORTED: 'border-dashed border-border bg-muted text-muted-foreground',
};

/** Qué le pide cada desenlace a quien lo lee. Los cuatro piden cosas distintas. */
export const KYC_OUTCOME_NOTE: Record<KycSubCheckOutcome, string> = {
    PASSED: 'La estación corrió y salió limpia. Es lo único que se puede leer como aprobado.',
    FAILED: 'La estación corrió y no aprobó. Es el único desenlace que señala un problema con el documento o con la persona.',
    HIT: 'El nombre apareció en una lista de vigilancia. No es un rechazo: es un caso que tiene que mirar un oficial de cumplimiento antes de decidir.',
    NOT_REPORTED:
        'De esta verificación no hay dato: no corrió en este flujo, o MetaMap devolvió algo que este contrato no sabe leer. Ni aprobó ni falló.',
};

/**
 * En qué quedó la prueba de vida de la sesión. La estación que el silo no mandó se lee
 * `NOT_REPORTED`: darla por aprobada inventaría una verificación que nadie corrió.
 */
export function livenessOutcomeOf(session: CustomerKycSession): KycSubCheckOutcome {
    return session.subChecks.find((check) => check.name === 'liveness')?.outcome ?? 'NOT_REPORTED';
}

/** En qué quedó el comprobante. El chip es binario; esto clasifica de quién es el problema. */
export type ProofOfAddressTone = 'ok' | 'review' | 'rejected' | 'pending' | 'error' | 'unknown';

export interface ProofOfAddressVerdict {
    label: string;
    note: string;
    tone: ProofOfAddressTone;
}

/**
 * Los doce desenlaces del comprobante, agrupados por lo que le PIDEN a quien lee la ficha. Los de
 * tubería (`*_ERROR`) se separan a propósito: ahí falló lo nuestro, no el comprobante del cliente.
 */
const PROOF_OF_ADDRESS_VERDICT: Record<string, ProofOfAddressVerdict> = {
    SUCCESS: {
        label: 'Domicilio verificado',
        note: 'El comprobante se leyó y su domicilio quedó resuelto a nivel calle y guardado con sus componentes.',
        tone: 'ok',
    },
    MEX_HELP_NEEDED: {
        label: 'Sin resolver a nivel calle',
        note: 'El comprobante se leyó, pero el domicilio mexicano no se resolvió a nivel calle. Quedó guardado el texto del recibo para que alguien lo revise a mano.',
        tone: 'review',
    },
    USA_HELP_NEEDED: {
        label: 'Domicilio de EE. UU. sin resolver',
        note: 'El comprobante se leyó, pero el domicilio estadounidense no se resolvió a nivel calle. Quedó guardado el texto del recibo para revisión manual.',
        tone: 'review',
    },
    COUNTRY_MISMATCH: {
        label: 'País declarado no coincide',
        note: 'El cliente declaró un país y su comprobante resolvió a otro. No es un rechazo: es un caso que hay que revisar antes de decidir.',
        tone: 'review',
    },
    COUNTRY_NOT_SUPPORTED: {
        label: 'País fuera de cobertura',
        note: 'El domicilio es de un país fuera de México y Estados Unidos. El comprobante se guardó, pero no alcanza para dar por cubierto el requisito.',
        tone: 'rejected',
    },
    PENDING_METAMAP_REVIEW: {
        label: 'En revisión de MetaMap',
        note: 'MetaMap mandó el comprobante a revisión manual y todavía no devolvió veredicto. Lo que se vea aquí puede cambiar.',
        tone: 'pending',
    },
    NO_ADDRESS_DETECTED: {
        label: 'Sin domicilio legible',
        note: 'La lectura no encontró ningún domicilio en el archivo: el comprobante es ilegible, o lo que se subió no es un comprobante.',
        tone: 'rejected',
    },
    PLACES_NO_MATCH: {
        label: 'Domicilio sin coincidencias',
        note: 'El domicilio leído no coincidió con ninguna dirección conocida: el texto es ilegible, o apunta fuera de los países que se consultan.',
        tone: 'rejected',
    },
    METAMAP_ERROR: {
        label: 'No se pudo traer la evidencia',
        note: 'No se alcanzó a traer la evidencia desde MetaMap. Falló la integración, no el comprobante del cliente: se puede reintentar.',
        tone: 'error',
    },
    PLACES_ERROR: {
        label: 'Falló el resolutor de domicilios',
        note: 'Falló el servicio que resuelve domicilios. El problema es de nuestra tubería, no del comprobante: se puede reintentar.',
        tone: 'error',
    },
    ADDRESS_SAVE_ERROR: {
        label: 'No se pudo guardar el domicilio',
        note: 'El domicilio se leyó bien y falló al guardarse. El problema es de nuestra tubería: se puede reintentar.',
        tone: 'error',
    },
    CHECKBOX_ERROR: {
        label: 'El domicilio quedó guardado',
        note: 'El domicilio se guardó y solo falló prender la casilla del requisito. El dato del cliente está a salvo: se puede reintentar.',
        tone: 'error',
    },
};

/**
 * El veredicto del comprobante. Un desenlace que este contrato no conoce se dice CRUDO y sin tono:
 * caer en `else` lo aprobaría o lo rechazaría, y de un valor que no sabemos leer no se sabe nada.
 */
export function proofOfAddressVerdict(
    proof: ProofOfAddressResult | null,
): ProofOfAddressVerdict | null {
    if (!proof) return null;
    return (
        PROOF_OF_ADDRESS_VERDICT[proof.status] ?? {
            label: 'Desenlace no reconocido',
            note: `El comprobante quedó en «${proof.status}», un desenlace que esta pantalla no sabe leer todavía. No se puede decir ni que sirvió ni que no.`,
            tone: 'unknown',
        }
    );
}

/**
 * El comprobante de domicilio como lo pide el mock: binario. `addressPersisted` responde «se tiene o
 * no se tiene» sin reimplementar los doce desenlaces, que quedan en el `note`.
 */
export function proofOfAddressChip(proof: ProofOfAddressResult | null): KycChipVerdict {
    const verdict = proofOfAddressVerdict(proof);
    if (!verdict || !proof) return PROOF_WITHOUT_DATA;

    return proof.addressPersisted
        ? { label: 'Registrado', note: verdict.note, tone: 'info' }
        : { label: 'No registrado', note: verdict.note, tone: 'warning' };
}

/** La sesión no dejó desenlace del comprobante: mismo chip que una estación sin reportar. */
const PROOF_WITHOUT_DATA: KycChipVerdict = {
    label: 'Sin dato',
    note: 'Esta sesión no dejó ningún desenlace del comprobante de domicilio: el paso no llegó a cerrarse. No es que el comprobante se haya rechazado.',
    tone: 'muted',
};

const KYC_SESSIONS_FAILURE_LABEL: Record<ReadFailure, string> = {
    forbidden: 'Sin permiso a las sesiones',
    'not-found': 'El wizard no tiene al cliente',
    error: 'No se leyeron las sesiones',
};

const KYC_SESSIONS_FAILURE_NOTE: Record<ReadFailure, string> = {
    forbidden:
        'Tu rol no incluye las sesiones de venta de este negocio, así que las verificaciones ni se pidieron. Es un permiso del silo del wizard, aparte del de la ficha.',
    'not-found':
        'El silo del wizard no conoce a este cliente en el negocio que se consultó. La lectura salió bien: no hay sesiones que devolver.',
    error: 'La lectura de las verificaciones falló. El resto de la ficha se leyó aparte y puede estar bien.',
};

/** El hueco de las verificaciones cuando su lectura —que es otra, y de otro silo— no salió. */
export function kycSessionsFailureGap(failure: ReadFailure): Gap {
    return { label: KYC_SESSIONS_FAILURE_LABEL[failure], note: KYC_SESSIONS_FAILURE_NOTE[failure] };
}

/* ================================================================================
   CHIPS QUE LA FICHA CONSTRUYE — la identificación y la autorización de buró no vienen
   como un veredicto: se derivan del expediente y del crédito.
   ================================================================================ */

/** El tono de un chip derivado, y lo que ese tono le pide a quien lo lee. */
export type KycChipTone = 'success' | 'info' | 'warning' | 'muted';

export interface KycChipVerdict {
    label: string;
    note: string;
    tone: KycChipTone;
}

export const KYC_CHIP_TONE_CLASS: Record<KycChipTone, string> = {
    success: 'border-success bg-success/10 text-success',
    info: 'border-info bg-info/10 text-info',
    warning: 'border-warning bg-warning/10 text-warning',
    // Mismo chip punteado que la estación sin reportar: las dos dicen que no hay dato.
    muted: 'border-dashed border-border bg-muted text-muted-foreground',
};

const ID_BOTH_SIDES: KycChipVerdict = {
    label: 'Frente + reverso registrados',
    note: 'El expediente de identidad tiene los dos lados del documento con el que se verificó al cliente.',
    tone: 'info',
};

const ID_ONLY_FRONT: KycChipVerdict = {
    label: 'Solo el frente',
    note: 'El expediente tiene el frente del documento y no el reverso. La verificación no llegó a subir los dos lados.',
    tone: 'warning',
};

const ID_ONLY_BACK: KycChipVerdict = {
    label: 'Solo el reverso',
    note: 'El expediente tiene el reverso del documento y no el frente. La verificación no llegó a subir los dos lados.',
    tone: 'warning',
};

/**
 * Qué lados de la identificación llegaron al expediente. `null` cuando no hay ninguno: ahí el
 * hueco lo dice el bloque del expediente, que sabe si está vacío o si no se pudo leer.
 */
export function idDocumentVerdict(documents: OnboardingDocument[]): KycChipVerdict | null {
    const kinds = new Set(documents.map((document) => document.kind));
    const hasFront = kinds.has('ID_FRONT');
    const hasBack = kinds.has('ID_BACK');

    if (hasFront && hasBack) return ID_BOTH_SIDES;
    if (hasFront) return ID_ONLY_FRONT;
    if (hasBack) return ID_ONLY_BACK;
    return null;
}

const BUREAU_SIGNED: KycChipVerdict = {
    label: 'Firmada digital',
    note: 'La autorización de consulta al SIC se firma junto con el contrato del crédito, con el OTP del último paso de la venta, y este cliente ya tiene un crédito con esa firma asentada. El PDF de la autorización todavía no se genera.',
    tone: 'success',
};

const BUREAU_UNSIGNED: KycChipVerdict = {
    label: 'Sin firmar',
    note: 'Este cliente tiene créditos y en ninguno quedó asentada la firma del contrato, que es la que lleva la autorización de consulta al SIC.',
    tone: 'warning',
};

/**
 * Si algún crédito del cliente quedó con el contrato firmado, que es lo que asienta la
 * autorización de buró. `null` sin créditos: no hay ninguno que autorizar.
 */
export function bureauAuthorizationVerdict(credits: LoanCreditResponse[]): KycChipVerdict | null {
    if (credits.length === 0) return null;
    const signed = credits.some((credit) => credit.activationChecklist.contractSigned);
    return signed ? BUREAU_SIGNED : BUREAU_UNSIGNED;
}

/** Los desenlaces de la lectura del crédito que dejan la autorización sin leer. */
export type CreditReadFailure = 'forbidden' | 'no-source' | 'error';

const CREDIT_FAILURE_GAP: Record<CreditReadFailure, Gap> = {
    forbidden: {
        label: 'Sin permiso al crédito',
        note: 'Tu rol no incluye los créditos de este cliente, y la firma del contrato —que es la que asienta la autorización— vive ahí. No se leyó: no es que no la haya firmado.',
    },
    'no-source': {
        label: 'Sin acreditado no se sabe',
        note: 'Este cliente no tiene acreditado en el silo de crédito, que es donde se asienta la firma del contrato. Sin ese registro no hay dónde buscar la autorización.',
    },
    error: {
        label: 'No se leyó el crédito',
        note: 'La lectura del crédito de este cliente falló, así que no se sabe si algún contrato quedó firmado. El resto de la ficha se leyó aparte.',
    },
};

/** El hueco de la autorización de buró cuando la lectura del crédito no salió. */
export function creditFailureGap(failure: CreditReadFailure): Gap {
    return CREDIT_FAILURE_GAP[failure];
}
